import Dexie, { Table } from 'dexie';
import { COACHING_SYNC_CONFIG } from '@taleemabad/shared';

export interface IAudioRecording {
  id?: number;
  fileName: string;
  blob: Blob;
  /** Ordered array of prior-session complete WebM/MP4 containers. Stored
   * separately to avoid binary-concatenating two complete containers (which
   * corrupts playback). The audio player chains priorBlobs[0..n] → blob via
   * MediaSource. Replaces the old single stitchBlob field to handle multiple
   * crashes during resumed recordings. */
  priorBlobs?: Blob[];
  referenceId?: string | number;
  duration?: number;
  status: 'recording' | 'pending' | 'syncing' | 'synced' | 'failed';
  retryCount?: number;
  lastError?: string;
  lastSyncAttempt?: number;
  createdAt: number;
}

export interface IAudioChunk {
  id?: number;
  referenceId: string;
  chunkIndex: number;
  blob: Blob;
  createdAt: number;
}

export class AudioRecordingDB extends Dexie {
  audioRecordings!: Table<any>;
  audioChunks!: Table<IAudioChunk>;

  constructor() {
    super('AudioRecordingDB');
    this.version(1).stores({
      audioRecordings: '++id, fileName, referenceId, status, createdAt',
    });
    this.version(2).stores({
      audioRecordings: '++id, fileName, referenceId, status, createdAt',
      audioChunks: '++id, referenceId, chunkIndex',
    });
    // Version 3: adds non-indexed stitchBlob field to IAudioRecording.
    // Store definitions are identical — Dexie only indexes explicitly listed
    // columns; blob fields are stored as-is without indexing.
    this.version(3).stores({
      audioRecordings: '++id, fileName, referenceId, status, createdAt',
      audioChunks: '++id, referenceId, chunkIndex',
    });
    // Version 4: replaces stitchBlob?: Blob with priorBlobs?: Blob[] to support
    // accumulating multiple prior-session containers across repeated crashes.
    // Store definitions are identical — priorBlobs is a non-indexed array field.
    this.version(4).stores({
      audioRecordings: '++id, fileName, referenceId, status, createdAt',
      audioChunks: '++id, referenceId, chunkIndex',
    });
  }

  async saveRecording(
    recording: Omit<IAudioRecording, 'createdAt' | 'id'>,
  ): Promise<number> {
    return await this.audioRecordings.add({
      ...recording,
      createdAt: Date.now(),
    });
  }

  async upsertRecordingByReferenceId(
    recording: Omit<IAudioRecording, 'createdAt' | 'id'>,
  ): Promise<number> {
    if (!recording.referenceId) {
      return this.saveRecording(recording);
    }

    const normalizedRefId = recording.referenceId.toString();

    return await this.transaction('rw', this.audioRecordings, async () => {
      const existing = await this.audioRecordings
        .where('referenceId')
        .equals(normalizedRefId)
        .first();

      if (existing?.id) {
        const updatePayload: Partial<IAudioRecording> = {
          fileName: recording.fileName,
          blob: recording.blob,
          status: recording.status,
          // Preserve the existing duration if the new value is 0/undefined —
          // prevents the stitch-config write (status='recording') from
          // overwriting a valid duration that was set in a prior onStop call.
          duration:
            recording.duration && recording.duration > 0
              ? recording.duration
              : existing.duration,
          referenceId: normalizedRefId,
        };
        if (recording.priorBlobs !== undefined) {
          updatePayload.priorBlobs = recording.priorBlobs;
        }
        await this.audioRecordings.update(existing.id, updatePayload);
        return existing.id;
      }

      return await this.audioRecordings.add({
        ...recording,
        referenceId: normalizedRefId,
        createdAt: Date.now(),
      });
    });
  }

  async getRecordingByFileName(
    fileName: string,
  ): Promise<IAudioRecording | undefined> {
    return await this.audioRecordings
      .where('fileName')
      .equals(fileName)
      .first();
  }

  async deleteRecording(id: number): Promise<void> {
    await this.audioRecordings.delete(id);
  }

  async deleteRecordingByFileName(fileName: string): Promise<void> {
    const recording = await this.getRecordingByFileName(fileName);
    if (recording?.id) {
      await this.deleteRecording(recording.id);
    }
  }

  async updateStatus(
    id: number,
    status: IAudioRecording['status'],
    error?: string,
  ): Promise<void> {
    const updates: Partial<IAudioRecording> = { status };
    if (status === 'failed') {
      const existing = await this.audioRecordings.get(id);
      updates.retryCount = (existing?.retryCount || 0) + 1;
      updates.lastError = error;
      updates.lastSyncAttempt = Date.now();
    } else if (status === 'syncing') {
      updates.lastSyncAttempt = Date.now();
    } else if (status === 'synced') {
      updates.retryCount = 0;
      updates.lastError = undefined;
    }
    await this.audioRecordings.update(id, updates);
  }

  async getAllPendingRecordings(): Promise<IAudioRecording[]> {
    return await this.audioRecordings
      .where('status')
      .anyOf('pending', 'recording')
      .toArray();
  }

  async getAllFailedRecordings(): Promise<IAudioRecording[]> {
    return await this.audioRecordings
      .where('status')
      .equals('failed')
      .toArray();
  }

  async getSyncableRecordings(): Promise<IAudioRecording[]> {
    // Get pending and failed recordings that haven't exceeded max retries
    const MAX_RETRIES = COACHING_SYNC_CONFIG.MAX_RETRIES;
    const STALE_SYNCING_MS =
      (COACHING_SYNC_CONFIG as any).STALE_SYNCING_MS ?? 300000; // 5 min default
    const recordings = await this.audioRecordings
      .where('status')
      .anyOf('pending', 'failed', 'syncing')
      .toArray();

    return recordings.filter((r) => {
      if ((r.retryCount || 0) >= MAX_RETRIES) return false;
      if (r.status === 'syncing') {
        const lastAttempt = r.lastSyncAttempt || r.createdAt;
        return Date.now() - lastAttempt > STALE_SYNCING_MS;
      }
      return true;
    });
  }

  async getOrphanedRecordings(): Promise<IAudioRecording[]> {
    const allRecordings = await this.audioRecordings.toArray();
    const { coachingDB } = await import('./coaching');

    const orphaned: IAudioRecording[] = [];

    for (const recording of allRecordings) {
      if (!recording.referenceId) {
        orphaned.push(recording);
        continue;
      }

      const observation = await coachingDB.observations.get(
        recording.referenceId.toString(),
      );

      if (!observation) {
        orphaned.push(recording);
      }
    }

    return orphaned;
  }

  async deleteOrphanedRecordings(): Promise<number> {
    const orphaned = await this.getOrphanedRecordings();
    const deletePromises = orphaned.map((rec) =>
      rec.id ? this.deleteRecording(rec.id) : Promise.resolve(),
    );
    await Promise.all(deletePromises);
    return orphaned.length;
  }

  async deleteRecordingByReferenceId(
    referenceId: string | number,
  ): Promise<void> {
    const normalizedRef = referenceId.toString();

    const recordings = await this.audioRecordings
      .where('referenceId')
      .equals(normalizedRef)
      .toArray();

    const deletePromises = recordings.map((rec) =>
      rec.id ? this.deleteRecording(rec.id) : Promise.resolve(),
    );

    await Promise.all(deletePromises);

    // Also clean up any incremental chunks
    await this.deleteChunksByReferenceId(normalizedRef);
  }

  async getRecordingByReferenceId(
    referenceId: string | number,
  ): Promise<IAudioRecording | undefined> {
    return await this.audioRecordings
      .where('referenceId')
      .equals(referenceId.toString())
      .first();
  }

  /**
   * Moves any recordings stuck in 'recording' status to 'pending'.
   * Should be called on app startup to 'rescue' recordings from crashed sessions.
   * Also assembles any orphaned chunks into the main recording blob.
   */
  async cleanupStaleRecordings(): Promise<number> {
    const stale = await this.audioRecordings
      .where('status')
      .anyOf('recording', 'syncing')
      .toArray();

    if (stale.length === 0) return 0;

    const promises = stale.map(async (rec) => {
      if (!rec.id) return;

      // Assemble any persisted chunks into the main blob before marking pending.
      // NOTE: We do NOT binary-concat the stitchBlob here — two complete WebM
      // containers concatenated = corrupted playback. The stitchBlob is kept
      // as a separate field and chained via MediaSource at playback time.
      if (rec.referenceId) {
        const normalizedRef = rec.referenceId.toString();
        const chunks = await this.getAllChunks(normalizedRef);
        if (chunks.length > 0) {
          const mimeType = rec.blob?.type || 'audio/webm';
          const chunksBlob = this.assembleFullBlob(chunks, mimeType);
          // New session chunks become the main blob; the existing blob (prior
          // session data) is appended to the priorBlobs chain so the player
          // can chain all prior sessions via MediaSource.
          const updateFields: Partial<IAudioRecording> = {
            blob: chunksBlob,
          };
          // If there was a main blob (prior session data), append it to the
          // priorBlobs array so all prior sessions are preserved in order.
          if (rec.blob && rec.blob.size > 0) {
            const existingPrior = rec.priorBlobs || [];
            updateFields.priorBlobs = [...existingPrior, rec.blob];
          } else if (rec.priorBlobs && rec.priorBlobs.length > 0) {
            // Blob is empty placeholder (chunk mode) but prior chain exists — preserve it.
            updateFields.priorBlobs = rec.priorBlobs;
          }
          await this.audioRecordings.update(rec.id, updateFields);
          await this.deleteChunksByReferenceId(normalizedRef);
        } else if (
          // GUARD: If there are zero chunks AND the blob is empty (0-byte
          // placeholder from an immediate app kill before first auto-save)
          // AND there are no prior session blobs, this recording has no
          // recoverable audio data. Delete it instead of promoting to 'pending',
          // which would cause the sync service to upload an empty file.
          (!rec.blob || rec.blob.size === 0) &&
          (!rec.priorBlobs || rec.priorBlobs.length === 0)
        ) {
          await this.deleteRecording(rec.id);
          await this.deleteChunksByReferenceId(normalizedRef);
          return; // Skip marking as 'pending' — recording is gone
        }
      }

      await this.updateStatus(rec.id, 'pending');
    });

    await Promise.all(promises);
    return stale.length;
  }

  // ─── Chunk-based incremental persistence ────────────────────────────

  /**
   * Appends a single small chunk blob for incremental auto-save.
   */
  async appendChunk(
    referenceId: string,
    chunkIndex: number,
    blob: Blob,
  ): Promise<number> {
    return await this.audioChunks.add({
      referenceId,
      chunkIndex,
      blob,
      createdAt: Date.now(),
    });
  }

  /**
   * Returns all chunks for a referenceId, ordered by chunkIndex.
   */
  async getAllChunks(referenceId: string): Promise<IAudioChunk[]> {
    return await this.audioChunks
      .where('referenceId')
      .equals(referenceId)
      .sortBy('chunkIndex');
  }

  /**
   * Deletes all chunks for a referenceId.
   */
  async deleteChunksByReferenceId(referenceId: string): Promise<void> {
    await this.audioChunks.where('referenceId').equals(referenceId).delete();
  }

  /**
   * Concatenates all chunk blobs into a single Blob (simple binary concat).
   * This is valid because all chunks come from the same MediaRecorder session
   * or are a stitch blob followed by same-format segments.
   */
  assembleFullBlob(chunks: IAudioChunk[], mimeType: string): Blob {
    const blobs = chunks.map((c) => c.blob);
    return new Blob(blobs, { type: mimeType });
  }
}

export const audioRecordingDB = new AudioRecordingDB();
